// Descripción declarativa de las credenciales por provider.
//
// `key` es el nombre exacto que espera el backend en el payload de
// accounts/setnewcredentials, así que no se puede cambiar sin tocar el API.
//
// `sections[].postTest` marca las secciones que solo aparecen cuando la
// conexión ya fue validada (validCredentials === true). Antes de esa fase
// el usuario todavía no tiene esos datos, y el backend no los exige.

const azure = {
  label: "Azure",
  hasConnectionTest: true,
  testLabel: "Test connection",
  credentialNote:
    "Client ID, Tenant ID and Client Secret identify your Azure Service Principal. With them we can read your costs; this is not your user password.",
  sections: [
    {
      id: "authorization",
      title: "Authorization (Azure AD)",
      fields: [
        {
          key: "clientId",
          label: "Client ID",
          placeholder: "Enter Client ID",
          validate: "guid",
          required: true,
          secret: false,
          span: 1,
        },
        {
          key: "tenantIdAzure",
          label: "Tenant ID",
          placeholder: "Enter Tenant ID",
          validate: "guid",
          required: true,
          secret: false,
          span: 1,
        },
        {
          key: "clientSecret",
          label: "Client Secret",
          placeholder: "Enter Client Secret",
          validate: "nonEmpty",
          required: true,
          secret: true,
          span: 1,
        },
      ],
    },
    {
      id: "subscription",
      title: "Subscription & billing",
      postTest: true,
      fields: [
        {
          key: "subscriptionId",
          label: "Subscription ID",
          placeholder: "Enter Subscription ID",
          validate: "guid",
          required: true,
          secret: false,
          span: 1,
        },
        {
          key: "billingAccount",
          label: "Billing Account",
          placeholder: "Enter Billing Account",
          validate: "guid",
          required: true,
          secret: false,
          span: 1,
        },
      ],
    },
  ],
};

const aws = {
  label: "AWS",
  hasConnectionTest: true,
  testLabel: "Test connection",
  credentialNote:
    "An IAM access key pair with read permissions for Cost Explorer and the cost reports it draws from.",
  sections: [
    {
      id: "authorization",
      title: "Authorization (AWS IAM)",
      fields: [
        {
          key: "accessKeyId",
          label: "Access Key ID",
          placeholder: "Enter Access Key ID",
          validate: "awsAccessKeyId",
          required: true,
          secret: false,
          span: 1,
        },
        {
          key: "secretAccessKey",
          label: "Secret Access Key",
          placeholder: "Enter Secret Access Key",
          validate: "nonEmpty",
          required: true,
          secret: true,
          span: 1,
        },
      ],
    },
  ],
};

const gcp = {
  label: "GCP",
  hasConnectionTest: true,
  testLabel: "Test connection",
  credentialNote:
    "A service account key in JSON format, with the BigQuery table where the exported costs land.",
  sections: [
    {
      id: "authorization",
      title: "Authorization (service account)",
      fields: [
        {
          key: "credentials",
          label: "Service account credentials",
          placeholder: '{ "type": "service_account", ... }',
          validate: "json",
          required: true,
          secret: true,
          multiline: true,
          span: 2,
          parse: "json",
        },
      ],
    },
    {
      id: "dataset",
      title: "BigQuery dataset",
      fields: [
        {
          key: "costtable",
          label: "BigQuery table id",
          placeholder: "project.dataset.table",
          validate: "nonEmpty",
          required: true,
          secret: false,
          span: 1,
        },
      ],
    },
  ],
};

const snowflake = {
  label: "Snowflake",
  hasConnectionTest: true,
  testLabel: "Test connection",
  credentialNote:
    "A Snowflake user with access to the warehouse holding your consumption data.",
  sections: [
    {
      id: "connection",
      title: "Connection",
      fields: [
        {
          key: "user",
          label: "User",
          placeholder: "Enter User",
          validate: "nonEmpty",
          required: true,
          secret: false,
          span: 1,
        },
        {
          key: "password",
          label: "Password",
          placeholder: "Enter Password",
          validate: "nonEmpty",
          required: true,
          secret: true,
          span: 1,
        },
        {
          key: "account",
          label: "URL Account",
          placeholder: "myorg-myaccount",
          validate: "snowflakeAccount",
          required: true,
          secret: false,
          span: 1,
        },
        {
          key: "warehouse",
          label: "Warehouse",
          placeholder: "Enter Warehouse",
          validate: "nonEmpty",
          required: true,
          secret: false,
          span: 1,
        },
      ],
    },
  ],
};

export const PROVIDER_SCHEMAS = {
  AWS: aws,
  Azure: azure,
  GCP: gcp,
  Snowflake: snowflake,
};

export const PROVIDER_NAMES = Object.keys(PROVIDER_SCHEMAS);

// Campos que se mandan al endpoint de test de conexión. Las secciones
// `postTest` quedan afuera a propósito: hoy el test solo recibe las
// credenciales, y meterle subscriptionId/billingAccount cambiaría el
// body del request. El `user` lo agrega cada thunk por su cuenta.
export const buildTestPayload = (schema, values) => {
  const payload = {};
  schema.sections
    .filter((section) => !section.postTest)
    .forEach((section) => {
      section.fields.forEach((field) => {
        payload[field.key] =
          field.parse === "json" ? JSON.parse(values[field.key].trim()) : values[field.key].trim();
      });
    });
  return payload;
};

// Payload de alta/edición. Las secciones `postTest` solo aportan claves
// cuando la fase de test ya pasó, igual que antes.
export const buildSubmitPayload = (schema, accountName, values) => {
  const data = {
    cloudProvider: schema.label,
    accountName,
  };
  schema.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (section.postTest && !values[field.key]) return;
      data[field.key] =
        field.parse === "json" ? JSON.parse(values[field.key].trim()) : values[field.key].trim();
    });
  });
  return data;
};

// Campos que el usuario tiene que completar sí o sí en cada fase.
export const requiredFields = (schema, { includePostTest }) => {
  const fields = [];
  schema.sections.forEach((section) => {
    if (section.postTest && !includePostTest) return;
    section.fields.filter((field) => field.required).forEach((field) => fields.push(field));
  });
  return fields;
};

export const allFields = (schema) =>
  schema.sections.reduce((acc, section) => acc.concat(section.fields), []);
